import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';
  const toggleMenu = () => setIsOpen(!isOpen);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleMenu();
    }
  };

  const handleLinkClick = () => {
    setIsOpen(false);
  };

  return (
    <nav className="sticky top-0 z-50 bg-white shadow-lg border-b border-gray-200" role="navigation" aria-label="Main navigation">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center flex-shrink-0">
            <Link to="/">
              <img
                src="/DataCareSolutionsMainLogoCroped.png"
                alt="Data Care Solutions - Home"
                className="h-14 w-auto sm:h-16 transition-transform duration-200 hover:scale-105"
              />
            </Link>
          </div>

          {/* Hamburger */}
          <div
            className="md:hidden"
            onClick={toggleMenu}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            role="button"
            aria-label="Toggle navigation menu"
            aria-expanded={isOpen}
            aria-controls="mobile-menu"
          >
            <div className="space-y-1.5 cursor-pointer p-2">
              <span className={`block w-7 h-0.5 bg-gray-700 transition-all duration-300 ${isOpen ? 'rotate-45 translate-y-2' : ''}`} aria-hidden="true"></span>
              <span className={`block w-7 h-0.5 bg-gray-700 transition-all duration-300 ${isOpen ? 'opacity-0' : ''}`} aria-hidden="true"></span>
              <span className={`block w-7 h-0.5 bg-gray-700 transition-all duration-300 ${isOpen ? '-rotate-45 -translate-y-2' : ''}`} aria-hidden="true"></span>
            </div>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-6">
            {isHome && (
              <ul className="flex space-x-6" role="menubar">
                {['home', 'about', 'services', 'faq', 'contact'].map((section) => (
                  <li key={section} role="none">
                    <a
                      href={`#${section}`}
                      className="text-gray-700 hover:text-blue-600 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 capitalize px-3 py-2 rounded-md transition-all duration-200 font-medium text-lg relative after:content-[''] after:absolute after:w-0 after:h-0.5 after:bg-blue-600 after:left-1/2 after:bottom-0 after:transition-all after:duration-300 hover:after:w-full hover:after:left-0"
                      role="menuitem"
                    >
                      {section}
                    </a>
                  </li>
                ))}
              </ul>
            )}
            <a
              href="tel:+19286070710"
              className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-2.5 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              aria-label="Call Data Care Solutions at 928-607-0710"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              (928) 607-0710
            </a>
          </div>
        </div>

        {/* Mobile Nav */}
        <div className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 pb-6' : 'max-h-0'}`}>
          <div
            id="mobile-menu"
            role="menu"
            aria-labelledby="mobile-menu-button"
            className="pt-4 border-t border-gray-200"
          >
            {isHome && (
              <ul className="space-y-3 mb-4">
                {['home', 'about', 'services', 'faq', 'contact'].map((section) => (
                  <li key={section} role="none">
                    <a
                      href={`#${section}`}
                      className="block text-gray-700 hover:text-blue-600 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 capitalize px-4 py-3 rounded-md transition-all duration-200 font-medium text-lg hover:bg-blue-50"
                      onClick={handleLinkClick}
                      role="menuitem"
                    >
                      {section}
                    </a>
                  </li>
                ))}
              </ul>
            )}
            <a
              href="tel:+19286070710"
              className="flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-3 rounded-full mx-4 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              aria-label="Call Data Care Solutions at 928-607-0710"
              onClick={handleLinkClick}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              Call (928) 607-0710
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;